import React, { useState, useEffect } from 'react';
import { db, storage } from './firebase'; 
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { MdClose, MdAdminPanelSettings, MdGroup, MdEdit, MdExitToApp, MdPersonAdd, MdPersonRemove, MdCheck, MdInfoOutline } from 'react-icons/md';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * EditGroupInfoSidebar component - Inline Styles Version
 */
const EditGroupInfoSidebar = ({ selectedChat, user, onClose, allUsers, onGroupUpdate }) => {
    // --- Local States ---
    const [groupName, setGroupName] = useState(selectedChat?.groupName || "Unnamed Group");
    const [groupIconUrl, setGroupIconUrl] = useState(selectedChat?.groupProfileImageUrl || 'https://placehold.co/100x100?text=Group');
    const [groupMembers, setGroupMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isAddingMembers, setIsAddingMembers] = useState(false);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
    const [notification, setNotification] = useState({ message: '', type: '' });

    // Responsive State
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    // Hover States for UI Interactivity
    const [hoveredBtn, setHoveredBtn] = useState(null);
    const [hoveredMember, setHoveredMember] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Reset Group Name/Icon State
    useEffect(() => {
        if (selectedChat) {
            setGroupName(selectedChat.groupName || "Unnamed Group");
            setGroupIconUrl(selectedChat.groupProfileImageUrl || 'https://placehold.co/100x100?text=Group');
        }
    }, [selectedChat]);

    const groupDetails = selectedChat?.isGroup ? {
        name: groupName,
        groupProfileImageUrl: groupIconUrl,
        adminId: selectedChat.adminId,
        participants: selectedChat.participants || [],
        memberCount: selectedChat.participants?.length || 0,
        chatId: selectedChat.id,
    } : null;

    const isCurrentUserAdmin = user.uid === groupDetails?.adminId;
    const currentUserProfile = allUsers.find(u => u.id === user.uid) || { fullName: 'You', userName: 'you' };
    const currentUserName = currentUserProfile.fullName || currentUserProfile.userName;

    // --- Notification Helper ---
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 3000);
    };

    // --- Fetch Member Profiles ---
    useEffect(() => {
        if (!groupDetails || groupDetails.participants.length === 0) {
            setLoading(false);
            return;
        }

        const fetchMemberProfiles = async () => {
            setLoading(true);
            const profiles = await Promise.all(
                groupDetails.participants.map(async (pId) => {
                    const localProfile = allUsers.find(u => u.id === pId);
                    if (localProfile) {
                        return { id: pId, ...localProfile, userName: localProfile.userName || `user_${pId.substring(0, 8)}` };
                    }
                    try {
                        const userDoc = await getDoc(doc(db, 'users', pId));
                        if (userDoc.exists()) {
                            return { id: pId, ...userDoc.data(), userName: userDoc.data().userName || `user_${pId.substring(0, 8)}` };
                        }
                    } catch (error) {
                        console.error("Error fetching member profile:", pId, error);
                    }
                    return { id: pId, fullName: 'Unknown User', userName: 'unknown' };
                })
            );
            setGroupMembers(profiles.filter(p => p.id));
            setLoading(false);
        };

        fetchMemberProfiles();
    }, [selectedChat.id, selectedChat.participants, allUsers]);

    // Sort Members
    const sortedMembers = [...groupMembers].sort((a, b) => {
        const aIsAdmin = a.id === groupDetails.adminId;
        const bIsAdmin = b.id === groupDetails.adminId;
        const aIsCurrentUser = a.id === user.uid;
        const bIsCurrentUser = b.id === user.uid;
        
        if (aIsAdmin && !bIsAdmin) return -1;
        if (!aIsAdmin && bIsAdmin) return 1;
        if (aIsCurrentUser && !aIsAdmin && !bIsCurrentUser) return -1;
        if (bIsCurrentUser && !bIsAdmin && !aIsCurrentUser) return 1;

        const nameA = a.fullName || a.userName || '';
        const nameB = b.fullName || b.userName || '';
        return nameA.localeCompare(nameB);
    });

    const getMemberDisplayName = (member) => {
        const displayName = member.fullName || member.userName || 'Unknown User';
        return member.id === user.uid ? `${displayName} (You)` : displayName;
    };

    // --- Logic Functions (Same as before) ---
    const sendSystemMessage = async (messageText) => {
        try {
            await addDoc(collection(db, 'chats', groupDetails.chatId, 'messages'), {
                senderId: 'SYSTEM',
                timestamp: serverTimestamp(),
                content: messageText,
                isSystemMessage: true,
                isGroup: true,
            });
        } catch (error) {
            console.error("Error sending system message:", error);
        }
    };
    
    const handleNameChange = async () => {
        const trimmedName = groupName.trim();
        if (!trimmedName || trimmedName === selectedChat.groupName) {
            setGroupName(selectedChat.groupName || "Unnamed Group");
            setIsEditingName(false);
            return;
        }
        try {
            setLoading(true);
            await updateDoc(doc(db, 'chats', groupDetails.chatId), { groupName: trimmedName });
            setGroupName(trimmedName); 
            const message = `${currentUserName} changed the group name to "${trimmedName}"`;
            await sendSystemMessage(message);
            showNotification("Group name updated successfully!", 'success');
            setIsEditingName(false);
            onGroupUpdate();
        } catch (error) {
            console.error("Error updating group name:", error);
            showNotification("Failed to update group name.", 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleIconChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            setLoading(true);
            const storageRef = ref(storage, `groupProfiles/${groupDetails.chatId}_${Date.now()}`);
            await uploadBytes(storageRef, file);
            const newIconUrl = await getDownloadURL(storageRef);
            await updateDoc(doc(db, 'chats', groupDetails.chatId), { groupProfileImageUrl: newIconUrl });
            setGroupIconUrl(newIconUrl);
            const message = `${currentUserName} changed the group icon`;
            await sendSystemMessage(message);
            showNotification("Group icon updated successfully!", 'success');
            onGroupUpdate();
        } catch (error) {
            console.error("Error updating group icon:", error);
            showNotification("Failed to update group icon.", 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveGroup = async () => {
        if (!window.confirm(`Are you sure you want to leave ${groupDetails.name}?`)) return;
        try {
            setLoading(true);
            const chatRef = doc(db, 'chats', groupDetails.chatId);
            await updateDoc(chatRef, { participants: arrayRemove(user.uid) });
            const message = `${currentUserName} left the group`;
            await sendSystemMessage(message);
            if (isCurrentUserAdmin && groupDetails.participants.length > 1) {
                const remainingParticipants = groupDetails.participants.filter(id => id !== user.uid);
                const newAdminId = remainingParticipants[0];
                await updateDoc(chatRef, { adminId: newAdminId });
                const newAdminProfile = allUsers.find(u => u.id === newAdminId) || { fullName: 'a new admin' };
                const newAdminName = newAdminProfile.fullName || newAdminProfile.userName;
                const adminMessage = `${newAdminName} is the new admin`;
                await sendSystemMessage(adminMessage);
            }
            onClose();
            onGroupUpdate(true);
        } catch (error) {
            console.error("Error leaving group:", error);
            showNotification("Failed to leave group.", 'error');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsersToAdd = allUsers.filter(u =>
        u.id !== user.uid && 
        !groupDetails.participants.includes(u.id) && 
        (u.fullName?.toLowerCase().includes(memberSearchTerm.toLowerCase()) || 
         u.userName?.toLowerCase().includes(memberSearchTerm.toLowerCase()))
    );

    const toggleUserToAdd = (memberId) => {
        setSelectedUsersToAdd(prev => 
            prev.includes(memberId) 
                ? prev.filter(id => id !== memberId) 
                : [...prev, memberId]
        );
    };

    const handleAddMembers = async () => {
        if (selectedUsersToAdd.length === 0) {
            setIsAddingMembers(false);
            return;
        }
        try {
            setLoading(true);
            const chatRef = doc(db, 'chats', groupDetails.chatId);
            await updateDoc(chatRef, { participants: arrayUnion(...selectedUsersToAdd) });
            const addedNames = selectedUsersToAdd.map(id => {
                const profile = allUsers.find(u => u.id === id) || { fullName: 'a user' };
                return profile.fullName || profile.userName || 'a user';
            }).join(', ');
            const message = `${currentUserName} added ${addedNames}`;
            await sendSystemMessage(message);
            showNotification("Members added successfully!", 'success');
            setSelectedUsersToAdd([]);
            setIsAddingMembers(false);
            onGroupUpdate();
        } catch (error) {
            console.error("Error adding members:", error);
            showNotification("Failed to add members.", 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (memberId, memberName) => {
        if (!isCurrentUserAdmin || memberId === user.uid) return;
        if (!window.confirm(`Are you sure you want to remove ${memberName} from the group?`)) return;
        try {
            setLoading(true);
            const chatRef = doc(db, 'chats', groupDetails.chatId);
            await updateDoc(chatRef, { participants: arrayRemove(memberId) });
            const message = `${currentUserName} removed ${memberName}`;
            await sendSystemMessage(message);
            showNotification(`${memberName} removed successfully!`, 'success');
            onGroupUpdate();
        } catch (error) {
            console.error("Error removing member:", error);
            showNotification(`Failed to remove ${memberName}.`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- Inline Styles ---
    const styles = {
        container: {
            width: isMobile ? '100%' : '320px',
            backgroundColor: 'white',
            boxShadow: '-4px 0 15px rgba(0,0,0,0.1)',
            height: '100%',
            borderLeft: '1px solid #f3f4f6',
            zIndex: 30,
            position: isMobile ? 'fixed' : 'static',
            right: 0,
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease-in-out',
        },
        notification: (type) => ({
            padding: '12px',
            fontSize: '0.875rem',
            fontWeight: '500',
            backgroundColor: type === 'success' ? '#dcfce7' : '#fee2e2',
            color: type === 'success' ? '#15803d' : '#b91c1c',
            display: 'flex',
            alignItems: 'center',
        }),
        header: {
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        headerTitle: {
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            margin: 0,
        },
        closeBtn: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '50%',
            color: hoveredBtn === 'closeMain' ? '#ef4444' : '#6b7280',
            backgroundColor: hoveredBtn === 'closeMain' ? '#fee2e2' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
        },
        contentArea: {
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
        },
        iconSection: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid #f3f4f6',
            paddingBottom: '16px',
        },
        iconWrapper: {
            position: 'relative',
            width: '80px',
            height: '80px',
        },
        groupIcon: {
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '3px solid #3b82f6',
        },
        editIconOverlay: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
            color: 'white',
            cursor: 'pointer',
            opacity: hoveredBtn === 'icon' ? 1 : 0,
            transition: 'opacity 0.2s',
        },
        section: {
            borderBottom: '1px solid #f3f4f6',
            paddingBottom: '12px',
        },
        label: {
            fontSize: '0.75rem',
            color: '#6b7280',
            fontWeight: '600',
            marginBottom: '4px',
            margin: 0,
        },
        nameDisplay: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        groupNameText: {
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            margin: 0,
        },
        editNameBtn: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '50%',
            color: '#6b7280',
            transition: 'color 0.2s',
        },
        editNameInputContainer: {
            display: 'flex',
            alignItems: 'center',
        },
        nameInput: {
            flex: 1,
            border: '1px solid #d1d5db',
            padding: '6px',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '500',
            marginRight: '8px',
            outline: 'none',
        },
        actionBtn: (type) => ({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px',
            fontSize: '1rem',
            fontWeight: 'bold',
            borderRadius: '8px',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: type === 'leave' ? '#fee2e2' : '#dbeafe', // Red light vs Blue light
            color: type === 'leave' ? '#dc2626' : '#2563eb',
            transition: 'background-color 0.2s',
            marginBottom: '8px',
        }),
        membersList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginTop: '8px',
        },
        memberItem: (isHovering) => ({
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: isHovering ? '#f9fafb' : 'transparent',
            transition: 'background-color 0.2s',
        }),
        memberImg: {
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: '12px',
            border: '1px solid #f3f4f6',
        },
        memberName: (isMe) => ({
            fontSize: '0.875rem',
            fontWeight: '500',
            color: isMe ? '#2563eb' : '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: isMe ? 'bold' : '500',
        }),
        removeMemberBtn: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '50%',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        // Modal Styles
        modalOverlay: {
            position: 'absolute',
            inset: 0,
            backgroundColor: 'white',
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '12px',
            marginBottom: '12px',
        },
        searchInput: {
            width: '100%',
            padding: '10px',
            marginBottom: '12px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            outline: 'none',
            boxSizing: 'border-box',
        },
        addMembersList: {
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        },
        addMemberBtn: {
            width: '100%',
            padding: '10px',
            marginTop: '16px',
            fontSize: '1rem',
            fontWeight: 'bold',
            borderRadius: '8px',
            border: 'none',
            cursor: (selectedUsersToAdd.length === 0 || loading) ? 'not-allowed' : 'pointer',
            backgroundColor: (selectedUsersToAdd.length > 0 && !loading) ? '#22c55e' : '#d1d5db',
            color: 'white',
        }
    };

    if (!groupDetails) return null;

    if (loading && !isAddingMembers && !isEditingName) {
         return (
            <div style={{...styles.container, justifyContent: 'center', alignItems: 'center'}}>
                <div style={{color: '#4b5563'}}>Loading Group Info...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            
            {/* Notification Bar */}
            {notification.message && (
                <div style={styles.notification(notification.type)}>
                    <MdInfoOutline size={18} style={{ marginRight: '8px' }} />
                    {notification.message}
                </div>
            )}

            {/* Header */}
            <div style={styles.header}>
                <h3 style={styles.headerTitle}>
                    <MdGroup style={{ marginRight: '8px', color: '#3b82f6' }} size={20} /> 
                    Group Info
                </h3>
                <button 
                    onClick={onClose} 
                    style={styles.closeBtn}
                    onMouseEnter={() => setHoveredBtn('closeMain')}
                    onMouseLeave={() => setHoveredBtn(null)}
                >
                    <MdClose size={20} />
                </button>
            </div>
            
            <div style={styles.contentArea}>
                
                {/* Group Profile/Icon Section */}
                <div style={styles.iconSection}>
                    <div 
                        style={styles.iconWrapper}
                        onMouseEnter={() => setHoveredBtn('icon')}
                        onMouseLeave={() => setHoveredBtn(null)}
                    >
                        <img
                            src={groupDetails.groupProfileImageUrl}
                            alt={`${groupDetails.name} Icon`}
                            style={styles.groupIcon}
                        />
                        {isCurrentUserAdmin && (
                            <>
                                <input
                                    type="file"
                                    accept="image/*"
                                    id="group-icon-upload"
                                    onChange={handleIconChange}
                                    style={{ display: 'none' }}
                                    disabled={loading}
                                />
                                <label 
                                    htmlFor="group-icon-upload" 
                                    style={styles.editIconOverlay}
                                    title="Change Group Icon"
                                >
                                    <MdEdit size={20} />
                                </label>
                            </>
                        )}
                    </div>
                </div>

                {/* Group Name Section */}
                <div style={styles.section}>
                    <h5 style={styles.label}>Group Name</h5>
                    {isEditingName && isCurrentUserAdmin ? (
                        <div style={styles.editNameInputContainer}>
                            <input
                                type="text"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                style={styles.nameInput}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleNameChange();
                                    if (e.key === 'Escape') {
                                        setGroupName(selectedChat.groupName || "Unnamed Group");
                                        setIsEditingName(false);
                                    }
                                }}
                                autoFocus
                                disabled={loading}
                            />
                            <button onClick={handleNameChange} style={{...styles.editNameBtn, color: '#16a34a'}} disabled={loading}>
                                <MdCheck size={20} />
                            </button>
                            <button onClick={() => { setGroupName(selectedChat.groupName || "Unnamed Group"); setIsEditingName(false); }} style={{...styles.editNameBtn, color: '#dc2626'}} disabled={loading}>
                                <MdClose size={20} />
                            </button>
                        </div>
                    ) : (
                        <div style={styles.nameDisplay}>
                            <h4 style={styles.groupNameText}>{groupDetails.name}</h4>
                            {isCurrentUserAdmin && (
                                <button 
                                    onClick={() => setIsEditingName(true)} 
                                    style={styles.editNameBtn}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                                    disabled={loading}
                                >
                                    <MdEdit size={18} />
                                </button>
                            )}
                        </div>
                    )}
                    <p style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '4px'}}>{groupDetails.memberCount} Members</p>
                </div>

                {/* Leave Group Button */}
                <button
                    onClick={handleLeaveGroup}
                    style={styles.actionBtn('leave')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                    disabled={loading}
                >
                    <MdExitToApp size={20} style={{ marginRight: '8px' }} />
                    Leave Group
                </button>

                {/* Add Members Button (Admin Only) */}
                {isCurrentUserAdmin && (
                    <button
                        onClick={() => setIsAddingMembers(true)}
                        style={styles.actionBtn('add')}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                        disabled={loading}
                    >
                        <MdPersonAdd size={20} style={{ marginRight: '8px' }} />
                        Add Members
                    </button>
                )}
                
                {/* Members List */}
                <div style={{ paddingTop: '8px' }}>
                    <h5 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#374151', marginBottom: '8px', margin: 0 }}>
                        Group Members
                    </h5>
                    <div style={styles.membersList}>
                        {sortedMembers.map((member) => {
                            const isGroupAdmin = member.id === groupDetails.adminId;
                            const isCurrentUser = member.id === user.uid;
                            const displayName = getMemberDisplayName(member);

                            return (
                                <div
                                    key={member.id}
                                    style={styles.memberItem(hoveredMember === member.id)}
                                    onMouseEnter={() => setHoveredMember(member.id)}
                                    onMouseLeave={() => setHoveredMember(null)}
                                >
                                    <img
                                        src={member.profileImageUrl || "https://placehold.co/36x36/f1f1f1/000000?text=U"}
                                        alt={`${displayName}'s Profile`}
                                        style={styles.memberImg}
                                    />
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                                        <span style={styles.memberName(isCurrentUser)}>
                                            {displayName}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {isGroupAdmin && (
                                                <MdAdminPanelSettings 
                                                    size={18} 
                                                    style={{ color: '#16a34a', marginLeft: '8px' }}
                                                    title="Group Admin" 
                                                />
                                            )}
                                            {isCurrentUserAdmin && !isGroupAdmin && (
                                                <button 
                                                    onClick={() => handleRemoveMember(member.id, member.fullName || member.userName)}
                                                    style={styles.removeMemberBtn}
                                                    title="Remove from group"
                                                    disabled={loading}
                                                >
                                                    <MdPersonRemove size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Add Members Modal/Sidebar Content */}
            {isAddingMembers && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalHeader}>
                        <h4 style={{ fontSize: '1.125rem', fontWeight: 'bold', margin: 0 }}>
                            Add Members to {groupDetails.name}
                        </h4>
                        <button 
                            onClick={() => setIsAddingMembers(false)} 
                            style={styles.closeBtn}
                        >
                            <MdClose size={20} />
                        </button>
                    </div>
                    
                    <input
                        type="text"
                        placeholder="Search users to add..."
                        value={memberSearchTerm}
                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                        style={styles.searchInput}
                        autoFocus
                    />
                    
                    <div style={styles.addMembersList}>
                        {filteredUsersToAdd.length > 0 ? (
                            filteredUsersToAdd.map((userItem) => (
                                <div 
                                    key={userItem.id} 
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                                        padding: '10px', borderRadius: '8px', cursor: 'pointer',
                                        backgroundColor: selectedUsersToAdd.includes(userItem.id) ? '#eff6ff' : 'white',
                                        border: '1px solid #f3f4f6'
                                    }}
                                    onClick={() => toggleUserToAdd(userItem.id)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                        <img
                                            src={userItem.profileImageUrl || "https://placehold.co/40x40"}
                                            alt="User"
                                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', marginRight: '12px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#1f2937', fontSize: '0.9rem' }}>
                                            {userItem.fullName || userItem.userName}
                                        </span>
                                    </div>
                                    <div style={{
                                        width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: selectedUsersToAdd.includes(userItem.id) ? '2px solid #2563eb' : '2px solid #9ca3af',
                                        backgroundColor: selectedUsersToAdd.includes(userItem.id) ? '#2563eb' : 'transparent',
                                        color: 'white'
                                    }}>
                                        {selectedUsersToAdd.includes(userItem.id) && <MdCheck size={14} />}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                                No users found to add.
                            </div>
                        )}
                    </div>

                    <button
                        onClick={handleAddMembers}
                        disabled={selectedUsersToAdd.length === 0 || loading}
                        style={styles.addMemberBtn}
                    >
                        Add {selectedUsersToAdd.length} Member(s)
                    </button>
                </div>
            )}
        </div>
    );
};

export default EditGroupInfoSidebar;                                          